import React, { useEffect } from 'react';
import { Select } from '@base-ui/react/select';
import { Icon } from '@components/icon/Icon';
import cx from 'classnames';
import { useNavigate } from 'react-router-dom';
import typography from '~styles/typography.scss';
import { useAuth } from '../context/AuthContext';
import { RESERVATIONS_SERVICE_PATH } from '../paths';
import { buildRestaurantOptions } from '@shared/utils/restaurantOptions';
import styles from './RestaurantSelector.scss';

export const RestaurantSelector = () => {
  const navigate = useNavigate();
  const { restaurants, selectedRestaurantId, updateSelectedRestaurantId } =
    useAuth();

  const restaurantOptions = buildRestaurantOptions(restaurants);

  // Auto-select first restaurant if none is selected and options are available
  useEffect(() => {
    if (!selectedRestaurantId && restaurantOptions.length > 0) {
      updateSelectedRestaurantId(restaurantOptions[0].value);
    }
  }, [
    selectedRestaurantId,
    restaurantOptions.length,
    updateSelectedRestaurantId,
  ]);

  if (restaurantOptions.length === 1) {
    return (
      <h1 className={cx(typography.h4, styles.restaurantName)}>
        {restaurantOptions[0].label}
      </h1>
    );
  }

  const handleOnChangeRestaurants = (selectedLabel: string | null): void => {
    if (selectedLabel !== null) {
      const selectedOption = restaurantOptions.find(
        (option) => option.label === selectedLabel,
      );
      if (selectedOption) {
        updateSelectedRestaurantId(selectedOption.value);
        navigate(RESERVATIONS_SERVICE_PATH);
      }
    }
  };

  const currentLabel = restaurantOptions.find(
    (option) => option.value === selectedRestaurantId,
  )?.label;

  return (
    <Select.Root
      items={restaurantOptions}
      value={currentLabel || null}
      onValueChange={handleOnChangeRestaurants}
    >
      <Select.Trigger className={styles.trigger}>
        <Select.Value className={typography.t1}>
          {currentLabel || restaurantOptions[0].label}
        </Select.Value>
        <Select.Icon
          render={() => <Icon name="chevron" className={styles.icon} />}
        />
      </Select.Trigger>
      <Select.Portal>
        <Select.Positioner
          className={styles.Positioner}
          side="bottom"
          align="start"
          sideOffset={8}
        >
          <Select.Popup className={styles.popup}>
            {restaurantOptions.map(({ label, value }) => (
              <Select.Item key={value} value={label} className={styles.option}>
                {label}
              </Select.Item>
            ))}
          </Select.Popup>
        </Select.Positioner>
      </Select.Portal>
    </Select.Root>
  );
};
